import { Nullable } from '@/typescriptHelpers';

export enum AppStoreAgeRatingValue {
    None = 'NONE',
    Infrequent = 'INFREQUENT',
    Frequent = 'FREQUENT',
}

export enum AgeRatingKidsAgeBand {
    FiveAndUnder = 'FIVE_AND_UNDER',
    SixToEight = 'SIX_TO_EIGHT',
    NineToEleven = 'NINE_TO_ELEVEN',
}

export enum AgeRatingOverride {
    None = 'NONE',
    SeventeenPlus = 'SEVENTEEN_PLUS',
    Unrated = 'UNRATED',
}

export interface AgeRatingDeclaration {
    // Frequency-style content descriptors.
    alcoholTobaccoOrDrugUseOrReferences?: Nullable<AppStoreAgeRatingValue>;
    contests?: Nullable<AppStoreAgeRatingValue>;
    gamblingSimulated?: Nullable<AppStoreAgeRatingValue>;
    gunsOrOtherWeapons?: Nullable<AppStoreAgeRatingValue>;
    horrorOrFearThemes?: Nullable<AppStoreAgeRatingValue>;
    matureOrSuggestiveThemes?: Nullable<AppStoreAgeRatingValue>;
    medicalOrTreatmentInformation?: Nullable<AppStoreAgeRatingValue>;
    profanityOrCrudeHumor?: Nullable<AppStoreAgeRatingValue>;
    sexualContentGraphicAndNudity?: Nullable<AppStoreAgeRatingValue>;
    sexualContentOrNudity?: Nullable<AppStoreAgeRatingValue>;
    violenceCartoonOrFantasy?: Nullable<AppStoreAgeRatingValue>;
    violenceRealistic?: Nullable<AppStoreAgeRatingValue>;
    violenceRealisticProlongedGraphicOrSadistic?: Nullable<AppStoreAgeRatingValue>;

    // Boolean capabilities / activities.
    advertising?: Nullable<boolean>;
    gambling?: Nullable<boolean>;
    healthOrWellnessTopics?: Nullable<boolean>;
    lootBox?: Nullable<boolean>;
    parentalControls?: Nullable<boolean>;
    unrestrictedWebAccess?: Nullable<boolean>;
    userGeneratedContent?: Nullable<boolean>;
    messagingAndChat?: Nullable<boolean>;

    // Social-media capability questions — the RUN-17925 Sept 2026 requirement.
    socialMedia?: Nullable<boolean>;
    socialMediaAgeRestricted?: Nullable<boolean>;
    ageAssurance?: Nullable<boolean>;

    // Made-for-Kids age band; null when the app is not a Kids app.
    kidsAgeBand?: Nullable<AgeRatingKidsAgeBand>;

    // Manual rating overrides.
    ageRatingOverride?: Nullable<AgeRatingOverride>;
    ageRatingOverrideV2?: Nullable<string>;
    koreaAgeRatingOverride?: Nullable<string>;
    developerAgeRatingInfoUrl?: Nullable<string>;
}

export enum AgeRatingQuestionKind {
    Frequency = 'frequency',
    Boolean = 'boolean',
}

export interface AgeRatingQuestion {
    key: keyof AgeRatingDeclaration;
    kind: AgeRatingQuestionKind;
    label: string;
    description?: string;
    tooltip?: string;
    helpText?: string;
    introducedInVersion: number;
}

export interface AgeRatingQuestionGroup {
    title: string;
    questions: AgeRatingQuestion[];
}

export interface AgeRatingFrequencyOption {
    value: AppStoreAgeRatingValue;
    label: string;
}

export interface AgeRatingQuestionSet {
    version: number;
    groups: AgeRatingQuestionGroup[];
    frequencyOptions: AgeRatingFrequencyOption[];
}

export interface AppAgeRatingDefault {
    declaration?: Nullable<AgeRatingDeclaration>;
    questionSetVersion?: number;
    ascDeclaration?: Nullable<AgeRatingDeclaration>;
    questionSet?: Nullable<AgeRatingQuestionSet>;
    hasAppStoreConnectDestination?: boolean;
}

export interface UpdateAppAgeRatingDefaultRequest {
    declaration?: Nullable<AgeRatingDeclaration>;
}

export const ageRatingNeedsAttention = (
    declaration: Nullable<AgeRatingDeclaration>,
    storedQuestionSetVersion: Nullable<number>,
    currentQuestionSetVersion: Nullable<number>,
): boolean => {
    if (!declaration) {
        // Never configured — surface it so teams set it up before the deadline.
        return true;
    }
    if (!currentQuestionSetVersion) {
        return false;
    }
    return (storedQuestionSetVersion ?? 0) < currentQuestionSetVersion;
};
